import { deepEqual } from 'fast-equals';
import { useCallback, useRef } from 'react';
import { atom, AtomEffect, atomFamily, AtomFamilyOptions, AtomOptions, DefaultValue, ReadOnlySelectorFamilyOptions, ReadOnlySelectorOptions, ReadWriteSelectorFamilyOptions, ReadWriteSelectorOptions, RecoilState, RecoilValue, RecoilValueReadOnly, selector, selectorFamily, SerializableParam, SetterOrUpdater, useRecoilCallback, useRecoilState, useRecoilTransaction_UNSTABLE, useRecoilValue } from 'recoil';

// TODO: Need to replace [helpers.ts] with this eventually

// ------------------------------------------------------------------------------------------------
// Type abbreviations
// ------------------------------------------------------------------------------------------------

// Type for IDs state
type IdsStateType<TID extends SerializableParam> = RecoilState<TID[]>;
// Type for Data state
type DataStateType<TID extends SerializableParam, TData> = (key: TID) => RecoilState<TData>;

// Signature for { get, set, reset }
type FnRecoilGetter = <U>(value: RecoilValue<U>) => U;
type FnRecoilSetter = <U>(state: RecoilState<U>, u: U | ((currVal: U) => U)) => void;
type FnRecoilResetter = <U>(state: RecoilState<U>) => void;

// Signature for function to get ID from Data
export type FnId<TID, TData> = (data: TData) => TID;
// Signature of function to check if two items are equal
export type FnEquals<T> = (a: T, b: T) => boolean;
// Signature of function to merge multiple items into a single item
export type FnMerge<T> = (itemA: T, itemB: T) => T;
// Signature of function to sort a list of items
export type FnSort<T> = (a: T, b: T) => number;

// ------------------------------------------------------------------------------------------------
// Constants
// ------------------------------------------------------------------------------------------------

// Default implementations of equality testers
export function FN_EQUALS_STRICT<T>(a: T, b: T): boolean {
  return a === b;
}
export function FN_EQUALS_DEEP<T>(a: T, b: T): boolean {
  return deepEqual(a, b);
}

// Default implementation of merging
export function FN_MERGE<T>(a: T, b: T): T {
  return { ...a, ...b };
}

// Default implementation of sorting
export function FN_SORT_NONE<T>(a: T, b: T): number {
  return 0;
}

// ------------------------------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------------------------------

/**
 * Typescript guard to determine whether a Recoil value is being set or reset. This is especially
 * useful in recoil `selector` and `selectorFamily` set methods. It also helps to narrow down types
 * in case of set.
 * ```
 * selector({
 *   set: ({ set, reset }, newValue) => {
 *     if (isRecoilDefaultValue(newValue)) {
 *       reset();
 *     } else {
 *       set(state, newValue);
 *     }
 *   }
 * })
 * ```
 */
export function isRecoilDefaultValue(value: unknown): value is DefaultValue {
  return value instanceof DefaultValue;
}

// Checks if a list has a given value
export function listHasValue<T>(list: T[], itemToFind: T, fnEquals: FnEquals<T>): boolean {
  return !!list.find((item) => fnEquals(item, itemToFind));
}

/**
 * Appends an item to the given list only if it does not already exist (prevents setting state needlessly)
 * ```
 * set(state, ids => appendValueIfNotExist(ids, newId, FN_STRICT_EQUALS));
 * ```
 */
export function appendValueIfNotExist<T>(list: T[], newItem: T, fnEquals: FnEquals<T>): T[] {
  if (listHasValue(list, newItem, fnEquals)) return list;
  return list.concat(newItem);
}

/**
 * Removes an item from the given list only if it already exists (prevents setting state needlessly)
 * ```
 * set(state, ids => removeValueIfExist(ids, delId, FN_STRICT_EQUALS));
 * ```
 */
export function removeValueIfExist<T>(list: T[], delItem: T, fnEquals: FnEquals<T>): T[] {
  if (!listHasValue(list, delItem, fnEquals)) return list;
  return list.filter((key) => !fnEquals(key, delItem));
}

/**
 * Appends only those items to the given list which do not already exist (prevents setting state needlessly)
 * ```
 * set(state, ids => appendAllValuesIfNotExist(ids, newIds, FN_STRICT_EQUALS));
 * ```
 */
export function appendAllValuesIfNotExist<T>(list: T[], newItems: T[], fnEquals: FnEquals<T>): T[] {
  if (newItems.every((item) => listHasValue(list, item, fnEquals))) return list;
  return list.concat(newItems.filter((item) => !listHasValue(list, item, fnEquals)));
}

/**
 * Removes only those items from the given list which already exist (prevents setting state needlessly)
 * ```
 * set(state, ids => removeAllValuesIfExist(ids, delIds, FN_STRICT_EQUALS));
 * ```
 */
export function removeAllValuesIfExist<T>(list: T[], delItems: T[], fnEquals: FnEquals<T>): T[] {
  if (!list.some((item) => listHasValue(delItems, item, fnEquals))) return list;
  return list.filter((item) => !listHasValue(delItems, item, fnEquals));
}

// ------------------------------------------------------------------------------------------------

/**
 * Convenience method to replace items in recoil states by adding, deleting and updating required items
 * ```
 * recoilReplaceList(get, set, reset, idsState, dataState, item => item.id, FN_EQUALS_STRICT, FN_EQUALS_DEEP, items);
 * ```
 */
export function recoilReplaceList<TID extends SerializableParam, TData>(
  //
  get: FnRecoilGetter,
  set: FnRecoilSetter,
  reset: FnRecoilResetter,
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnId: FnId<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  fnDataEquals: FnEquals<TData>,
  items: TData[]
) {
  const oldIds = get(idsState);
  const newIds = items.map(fnId);
  const oldItems = oldIds.map(dataState).map(get);
  const newItems = items;
  const itemsAdded = newItems.filter((item) => !listHasValue(oldIds, fnId(item), fnIdEquals));
  const itemsDeleted = oldItems.filter((item) => !listHasValue(newIds, fnId(item), fnIdEquals));
  const itemsUpdated = newItems.filter((item) => listHasValue(oldIds, fnId(item), fnIdEquals) && !fnDataEquals(item, get(dataState(fnId(item)))));
  if (itemsAdded.length || itemsDeleted.length) {
    set(idsState, newIds);
  }
  itemsDeleted.forEach((item) => reset(dataState(fnId(item))));
  itemsAdded.forEach((item) => set(dataState(fnId(item)), item));
  itemsUpdated.forEach((item) => set(dataState(fnId(item)), item));
}

/**
 * Convenience method to upsert items in recoil states by adding and updating required items (no deletion)
 * ```
 * recoilUpsertList(get, set, reset, idsState, dataState, item => item.id, FN_EQUALS_STRICT, FN_EQUALS_DEEP, items);
 * ```
 */
export function recoilUpsertList<TID extends SerializableParam, TData>(
  //
  get: FnRecoilGetter,
  set: FnRecoilSetter,
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnId: FnId<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  fnDataEquals: FnEquals<TData>,
  items: TData[]
) {
  const oldIds = get(idsState);
  const newItems = items;
  const itemsAdded = newItems.filter((item) => !listHasValue(oldIds, fnId(item), fnIdEquals));
  const itemsUpdated = newItems.filter((item) => listHasValue(oldIds, fnId(item), fnIdEquals) && !fnDataEquals(item, get(dataState(fnId(item)))));
  if (itemsAdded.length) {
    set(idsState, oldIds.concat(itemsAdded.map(fnId)));
  }
  itemsAdded.forEach((item) => set(dataState(fnId(item)), item));
  itemsUpdated.forEach((item) => set(dataState(fnId(item)), item));
}

/**
 * Convenience method to merge items in recoil states by adding and updating required items (no deletion)
 * ```
 * recoilMergeList(get, set, reset, idsState, dataState, item => item.id, FN_EQUALS_STRICT, FN_EQUALS_DEEP, fnMerge, items);
 * ```
 */
export function recoilMergeList<TID extends SerializableParam, TData>(
  //
  get: FnRecoilGetter,
  set: FnRecoilSetter,
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnId: FnId<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  fnDataEquals: FnEquals<TData>,
  fnMerge: FnMerge<TData>,
  items: TData[]
) {
  const oldIds = get(idsState);
  const newItems = items;
  const itemsAdded = newItems.filter((item) => !listHasValue(oldIds, fnId(item), fnIdEquals));
  const itemsUpdated = newItems.filter((item) => listHasValue(oldIds, fnId(item), fnIdEquals) && !fnDataEquals(item, get(dataState(fnId(item)))));
  if (itemsAdded.length) {
    set(idsState, oldIds.concat(itemsAdded.map(fnId)));
  }
  itemsAdded.forEach((item) => set(dataState(fnId(item)), item));
  itemsUpdated.forEach((item) => set(dataState(fnId(item)), (oldItem) => fnMerge(oldItem, item)));
}

/**
 * Convenience method to delete items from recoil states (no adding or updating)
 * ```
 * recoilListRemove(get, set, reset, idsState, dataState, item => item.id, FN_EQUALS_STRICT, items);
 * ```
 */
export function recoilListRemove<TID extends SerializableParam, TData>(
  //
  get: FnRecoilGetter,
  set: FnRecoilSetter,
  reset: FnRecoilResetter,
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  ids: TID[]
) {
  const oldIds = get(idsState);
  const newIds = ids;
  const idsDeleted = newIds.filter((key) => listHasValue(oldIds, key, fnIdEquals));
  if (idsDeleted.length) {
    set(
      idsState,
      oldIds.filter((key) => !listHasValue(idsDeleted, key, fnIdEquals))
    );
  }
  idsDeleted.forEach((key) => reset(dataState(key)));
}

// ------------------------------------------------------------------------------------------------

/**
 * Hook to replace items in recoil states by adding, deleting and updating required items
 * ```
 * const hook = useRecoilTransactionReplaceList(idsState, dataState, item => item.id, FN_EQUALS_STRICT, FN_EQUALS_DEEP);
 * hook(list);
 * ```
 */
export function useRecoilTransactionReplaceList<TID extends SerializableParam, TData>(
  //
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnId: FnId<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  fnDataEquals: FnEquals<TData>
) {
  return useRecoilTransaction_UNSTABLE(
    // eslint-disable-next-line @typescript-eslint/unbound-method
    ({ get, set, reset }) =>
      (items: TData[]) => {
        recoilReplaceList(get, set, reset, idsState, dataState, fnId, fnIdEquals, fnDataEquals, items);
      },
    []
  );
}

/**
 * Hook to upsert items in recoil states by adding and updating required items (no deletion)
 * ```
 * const hook = useRecoilTransactionUpsertList(idsState, dataState, item => item.id, FN_EQUALS_STRICT, FN_EQUALS_DEEP);
 * hook(list);
 * ```
 */
export function useRecoilTransactionUpsertList<TID extends SerializableParam, TData>(
  //
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnId: FnId<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  fnDataEquals: FnEquals<TData>
) {
  return useRecoilTransaction_UNSTABLE(
    // eslint-disable-next-line @typescript-eslint/unbound-method
    ({ get, set }) =>
      (items: TData[]) => {
        recoilUpsertList(get, set, idsState, dataState, fnId, fnIdEquals, fnDataEquals, items);
      },
    []
  );
}

/**
 * Hook to merge items in recoil states by adding and updating required items (no deletion)
 * ```
 * const hook = useRecoilTransactionMergeList(idsState, dataState, item => item.id, FN_EQUALS_STRICT, FN_EQUALS_DEEP, fnMerge);
 * hook(list);
 * ```
 */
export function useRecoilTransactionMergeList<TID extends SerializableParam, TData>(
  //
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnId: FnId<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  fnDataEquals: FnEquals<TData>,
  fnMerge: FnMerge<TData>
) {
  return useRecoilTransaction_UNSTABLE(
    // eslint-disable-next-line @typescript-eslint/unbound-method
    ({ get, set }) =>
      (items: TData[]) => {
        recoilMergeList(get, set, idsState, dataState, fnId, fnIdEquals, fnDataEquals, fnMerge, items);
      },
    []
  );
}

/**
 * Hook to delete items from recoil states (no adding or updating)
 * ```
 * const hook = useRecoilTransactionRemoveList(idsState, dataState, item => item.id, FN_EQUALS_STRICT);
 * hook(ids);
 * ```
 */
export function useRecoilTransactionRemoveList<TID extends SerializableParam, TData>(
  //
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnIdEquals: FnEquals<TID>
) {
  return useRecoilTransaction_UNSTABLE(
    // eslint-disable-next-line @typescript-eslint/unbound-method
    ({ get, set, reset }) =>
      (ids: TID[]) => {
        recoilListRemove(get, set, reset, idsState, dataState, fnIdEquals, ids);
      },
    []
  );
}

// ------------------------------------------------------------------------------------------------

/**
 * Convenience method to help reset recoil states
 * ```
 * resetSelector(set, reset, idsState, dataState, FN_EQUALS_STRICT, delId);
 * ```
 */
export function resetSelector<TID extends SerializableParam, TData>(
  //
  set: FnRecoilSetter,
  reset: FnRecoilResetter,
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  delKey: TID
) {
  set(idsState, (oldIds) => removeValueIfExist(oldIds, delKey, fnIdEquals));
  reset(dataState(delKey));
}

/**
 * Convenience method to help set recoil states
 * ```
 * setSelector(set, reset, idsState, dataState, item => item.id, FN_EQUALS_STRICT, FN_EQUALS_STRICT, newItem);
 * ```
 */
export function setSelector<TID extends SerializableParam, TData>(
  //
  set: FnRecoilSetter,
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnId: FnId<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  fnDataEquals: FnEquals<TData>,
  item: TData
) {
  const key = fnId(item);
  set(idsState, (oldIds) => appendValueIfNotExist(oldIds, key, fnIdEquals));
  set(dataState(key), (oldItem) => (fnDataEquals(oldItem, item) ? oldItem : item));
}

// ------------------------------------------------------------------------------------------------

/**
 * Makes a getter for a recoil selector family
 * ```
 * const state = selectorFamily<Object, string>({
 *   key: '',
 *   get: makeSelectorGet(dataState)
 * });
 * ```
 */
export function makeSelectorFamilyGet<TID extends SerializableParam, TData>(
  //
  dataState: DataStateType<TID, TData>
) {
  return (key: TID) =>
    ({ get }: { get: FnRecoilGetter }) =>
      get(dataState(key));
}

/**
 * Makes a setter for a recoil selector family
 * ```
 * const state = selectorFamily<Object, string>({
 *   key: '',
 *   set: makeSelectorSet(idsState, dataState, item => item.id, FN_EQUALS_STRICT, FN_EQUALS_DEEP)
 * });
 * ```
 */
export function makeSelectorFamilySet<TID extends SerializableParam, TData>(
  //
  idsState: IdsStateType<TID>,
  dataState: DataStateType<TID, TData>,
  fnId: FnId<TID, TData>,
  fnIdEquals: FnEquals<TID>,
  fnDataEquals: FnEquals<TData>
) {
  return (key: TID) =>
    ({ set, reset }: { set: FnRecoilSetter; reset: FnRecoilResetter }, newValue: TData | DefaultValue) => {
      if (isRecoilDefaultValue(newValue)) {
        resetSelector(set, reset, idsState, dataState, fnIdEquals, key);
      } else {
        setSelector(set, idsState, dataState, fnId, fnIdEquals, fnDataEquals, newValue);
      }
    };
}

// ------------------------------------------------------------------------------------------------

/**
 * Quickly creates all possible recoil atoms, selectors and hooks for a given scenario
 * ```
 * const {
 *   idsState,
 *   dataState,
 *   state,
 *   listState,
 *   listSortedState,
 *   useGetByIdHook,
 *   useSetHook,
 *   useResetByIdHook,
 *   useGetListHook,
 *   useGetListSortedHook,
 *   useReplaceListHook,
 *   useUpsertListHook,
 *   useMergeListHook,
 *   useRemoveListByIdsHook
 * } = makeRecoilStates('prefix', DEFAULT_VALUE, item => item.id, FN_EQUALS_STRICT, FN_EQUALS_DEEP, fnSort);
 * ```
 */
export function makeRecoilStates<TID extends SerializableParam, TData>(
  //
  prefix: string,
  defaultValue: TData,
  fnId: FnId<TID, TData>,
  fnIdEquals: FnEquals<TID> = FN_EQUALS_STRICT,
  fnDataEquals: FnEquals<TData> = FN_EQUALS_DEEP,
  fnSort: FnSort<TData> = FN_SORT_NONE,
  fnMerge: FnMerge<TData> = FN_MERGE
) {
  const idsState = atom<TID[]>({
    key: `${prefix}IDs`,
    default: []
  });

  const dataState = atomFamily<TData, TID>({
    key: `${prefix}Data`,
    default: defaultValue
  });

  const state = selectorFamily<TData, TID>({
    key: `${prefix}`,
    get: makeSelectorFamilyGet(dataState),
    set: makeSelectorFamilySet(idsState, dataState, fnId, fnIdEquals, fnDataEquals)
  });

  const listState = selector<TData[]>({
    key: `${prefix}List`,
    get: ({ get }) => get(idsState).map(dataState).map(get)
  });

  const sortedListState = selector<TData[]>({
    key: `${prefix}SortedList`,
    get: ({ get }) => [...get(listState)].sort(fnSort)
  });

  const useGetByIdHook = () =>
    useRecoilCallback(
      ({ snapshot }) =>
        (id: TID) =>
          snapshot.getLoadable(dataState(id)).getValue(),
      []
    );

  const useSetHook = () =>
    useRecoilCallback(
      ({ set }) =>
        (item: TData) =>
          set(state(fnId(item)), item),
      []
    );

  const useResetByIdHook = () =>
    useRecoilCallback(
      ({ reset }) =>
        (id: TID) =>
          reset(state(id)),
      []
    );

  const useGetListHook = () =>
    useRecoilCallback(
      ({ snapshot }) =>
        () => {
          const ids = snapshot.getLoadable(idsState).valueOrThrow();
          const list = ids.map((id) => snapshot.getLoadable(dataState(id)).valueOrThrow());
          return list;
        },
      []
    );

  const useGetSortedListHook = () =>
    useRecoilCallback(
      ({ snapshot }) =>
        () => {
          const ids = snapshot.getLoadable(idsState).valueOrThrow();
          const list = ids.map((id) => snapshot.getLoadable(dataState(id)).valueOrThrow());
          return list.sort(fnSort);
        },
      []
    );

  const useReplaceListHook = () => useRecoilTransactionReplaceList(idsState, dataState, fnId, fnIdEquals, fnDataEquals);
  const useUpsertListHook = () => useRecoilTransactionUpsertList(idsState, dataState, fnId, fnIdEquals, fnDataEquals);
  const useMergeListHook = () => useRecoilTransactionMergeList(idsState, dataState, fnId, fnIdEquals, fnDataEquals, fnMerge);
  const useRemoveListByIdsHook = () => useRecoilTransactionRemoveList(idsState, dataState, fnIdEquals);

  return {
    idsState,
    dataState,
    state,
    listState,
    sortedListState,
    useGetByIdHook,
    useSetHook,
    useResetByIdHook,
    useGetListHook,
    useGetSortedListHook,
    useReplaceListHook,
    useUpsertListHook,
    useMergeListHook,
    useRemoveListByIdsHook
  };
}

// ------------------------------------------------------------------------------------------------

/**
 * Recoil effect to persist/load data to/from LocalStorage
 * ```
 * const state = atom<string, atom>({
 *   key: 'name',
 *   default: DEFAULT_VALUE,
 *   effects: [localStorageEffect('key')]
 * });
 * ```
 */
export function localStorageEffect<T>(key: string): AtomEffect<T> {
  return ({ onSet, resetSelf, setSelf }) => {
    const savedValue = localStorage.getItem(key);
    if (savedValue !== null) {
      const data = JSON.parse(savedValue);
      setSelf(data);
      logger.debug(`%cLOCAL_STORAGE :: Load [${key}]`, { extra: { data } });
    } else {
      resetSelf();
      logger.debug(`%cLOCAL_STORAGE :: Load [${key}]`, { extra: { data: null } });
    }
    onSet((newValue, oldValue, isReset) => {
      if (!isReset) {
        localStorage.setItem(key, JSON.stringify(newValue));
        logger.debug(`%cLOCAL_STORAGE :: Save [${key}]`, { extra: { oldValue, newValue } });
      } else {
        localStorage.removeItem(key);
        logger.debug(`%cLOCAL_STORAGE :: Reset [${key}]`, { extra: { oldValue } });
      }
    });
  };
}

/**
 * Recoil effect to persist/load data to/from SessionStorage
 * ```
 * const state = atom<string, atom>({
 *   key: 'name',
 *   default: DEFAULT_VALUE,
 *   effects: [sessionStorageEffect('key')]
 * });
 * ```
 */
export function sessionStorageEffect<T>(key: string): AtomEffect<T> {
  return ({ onSet, resetSelf, setSelf }) => {
    const savedValue = sessionStorage.getItem(key);
    if (savedValue !== null) {
      const data = JSON.parse(savedValue);
      setSelf(data);
      logger.debug(`%cSESSION_STORAGE :: Load [${key}]`, { extra: { data } });
    } else {
      resetSelf();
      logger.debug(`%cSESSION_STORAGE :: Load [${key}]`, { extra: { data: null } });
    }
    onSet((newValue, oldValue, isReset) => {
      if (!isReset) {
        sessionStorage.setItem(key, JSON.stringify(newValue));
        logger.debug(`%cSESSION_STORAGE :: Save [${key}]`, { extra: { oldValue, newValue } });
      } else {
        sessionStorage.removeItem(key);
        logger.debug(`%cSESSION_STORAGE :: Reset [${key}]`, { extra: { oldValue } });
      }
    });
  };
}

/*
Equal Recoil Start
*/

type EqualOptions<T> = {
  fnEquals?: FnEquals<T>;
};

export function equalAtom<T>({ key, fnEquals = FN_EQUALS_DEEP, ...options }: AtomOptions<T> & EqualOptions<T>): RecoilState<T> {
  const innerAtom = atom<T>({ key: `__${key}`, ...options });
  const outerSelector = selector<T>({
    key,
    get: ({ get }) => get(innerAtom),
    set: ({ get, set, reset }, newValue) => {
      if (isRecoilDefaultValue(newValue)) {
        reset(innerAtom);
      } else {
        const prevValue = get(innerAtom);
        if (!fnEquals(newValue, prevValue)) {
          set(innerAtom, newValue);
        }
      }
    }
  });
  return outerSelector;
}

export function equalSelectorReadonly<T>({ key, fnEquals = FN_EQUALS_DEEP, ...options }: ReadOnlySelectorOptions<T> & EqualOptions<T>): RecoilValueReadOnly<T> {
  let prevValue: T | undefined;
  const innerSelector = selector<T>({ key: `__${key}`, ...options });
  const outerSelector = selector<T>({
    key,
    get: ({ get }) => {
      const newValue = get(innerSelector);
      if (prevValue !== undefined && fnEquals(newValue, prevValue)) {
        return prevValue;
      } else {
        prevValue = newValue;
        return newValue;
      }
    }
  });
  return outerSelector;
}
export function equalSelectorReadWrite<T>({ key, fnEquals = FN_EQUALS_DEEP, ...options }: ReadWriteSelectorOptions<T> & EqualOptions<T>): RecoilState<T> {
  let prevValue: T | undefined;
  const innerSelector = selector<T>({ key: `__${key}`, ...options });
  const outerSelector = selector<T>({
    key,
    get: ({ get }) => {
      const newValue = get(innerSelector);
      if (prevValue !== undefined && fnEquals(newValue, prevValue)) {
        return prevValue;
      } else {
        prevValue = newValue;
        return newValue;
      }
    },
    set: ({ get, set, reset }, newValue) => {
      if (isRecoilDefaultValue(newValue)) {
        reset(innerSelector);
        prevValue = undefined;
      } else {
        if (!fnEquals(newValue, get(innerSelector))) {
          set(innerSelector, newValue);
          prevValue = newValue;
        }
      }
    }
  });
  return outerSelector;
}

export function equalAtomFamily<T, P extends SerializableParam>({ key, fnEquals = FN_EQUALS_DEEP, ...options }: AtomFamilyOptions<T, P> & EqualOptions<T>): (param: P) => RecoilState<T> {
  const innerAtomFamily = atomFamily<T, P>({ key: `__${key}`, ...options });
  const outerSelectorFamily = selectorFamily<T, P>({
    key,
    get:
      (id) =>
      ({ get }) =>
        get(innerAtomFamily(id)),
    set:
      (id) =>
      ({ get, set, reset }, newValue) => {
        if (isRecoilDefaultValue(newValue)) {
          reset(innerAtomFamily(id));
        } else {
          const prevValue = get(innerAtomFamily(id));
          if (!fnEquals(newValue, prevValue)) {
            set(innerAtomFamily(id), newValue);
          }
        }
      }
  });
  return outerSelectorFamily;
}

export function equalSelectorFamilyReadonly<T, P extends SerializableParam>({ key, fnEquals = FN_EQUALS_DEEP, ...options }: ReadOnlySelectorFamilyOptions<T, P> & EqualOptions<T>): (param: P) => RecoilValueReadOnly<T> {
  const prevValuesMap = new Map<P, T>();
  const innerSelectorFamily = selectorFamily<T, P>({ key: `__${key}`, ...options });
  const outerSelectorFamily = selectorFamily<T, P>({
    key,
    get:
      (id) =>
      ({ get }) => {
        const newValue = get(innerSelectorFamily(id));
        const prevValue = prevValuesMap.get(id);
        if (prevValue !== undefined && fnEquals(newValue, prevValue)) {
          return prevValue;
        } else {
          prevValuesMap.set(id, newValue);
          return newValue;
        }
      }
  });
  return outerSelectorFamily;
}
export function equalSelectorFamilyReadWrite<T, P extends SerializableParam>({ key, fnEquals = FN_EQUALS_DEEP, ...options }: ReadWriteSelectorFamilyOptions<T, P> & EqualOptions<T>): (param: P) => RecoilState<T> {
  const prevValuesMap = new Map<P, T>();
  const innerSelectorFamily = selectorFamily<T, P>({ key: `__${key}`, ...options });
  const outerSelectorFamily = selectorFamily<T, P>({
    key,
    get:
      (id) =>
      ({ get }) => {
        const newValue = get(innerSelectorFamily(id));
        const prevValue = prevValuesMap.get(id);
        if (prevValue !== undefined && fnEquals(newValue, prevValue)) {
          return prevValue;
        } else {
          prevValuesMap.set(id, newValue);
          return newValue;
        }
      },
    set:
      (id) =>
      ({ set, reset }, newValue) => {
        if (isRecoilDefaultValue(newValue)) {
          reset(innerSelectorFamily(id));
          prevValuesMap.delete(id);
        } else {
          set(innerSelectorFamily(id), newValue);
          prevValuesMap.set(id, newValue);
        }
      }
  });
  return outerSelectorFamily;
}

export type FnUpdater<T> = (currVal: T) => T;
export function isUpdater<T>(valOrUpdater: T | FnUpdater<T>): valOrUpdater is FnUpdater<T> {
  return typeof valOrUpdater === 'function';
}
export function evaluateValOrUpdater<T>(valOrUpdater: T | FnUpdater<T>, prevValue: T): T {
  return !isUpdater(valOrUpdater) ? valOrUpdater : valOrUpdater(prevValue);
}

/**
 * Cached version of `useRecoilValue` which returns cached value depending on equality method given.
 */
export function useEqualRecoilValue<T>(recoilValue: RecoilValue<T>, fnEquals: FnEquals<T> = FN_EQUALS_DEEP): T {
  // Stores previous value
  const prevValueRef = useRef<T>();
  // Actual recoil value
  const newValue = useRecoilValue(recoilValue);
  // Returns either previous or current value based on equality comparison
  if (prevValueRef.current === undefined || !fnEquals(prevValueRef.current, newValue)) {
    prevValueRef.current = newValue;
    return newValue;
  } else {
    return prevValueRef.current;
  }
}

/**
 * Cached version of `useRecoilState` which returns cached value depending on equality method given.
 */
export function useEqualRecoilState<T>(recoilState: RecoilState<T>, fnEquals: FnEquals<T> = FN_EQUALS_DEEP): [T, SetterOrUpdater<T>] {
  // Stores previous value
  const prevValueRef = useRef<T>();
  // Actual recoil state
  const [newValue, setNewValue] = useRecoilState(recoilState);
  // Custom setter that only sets when equality comparison fails
  const setNewValueWrapped: SetterOrUpdater<T> = useCallback(
    (valOrUpdater: T | ((currVal: T) => T)) => {
      setNewValue((oldValue) => {
        const value = isUpdater(valOrUpdater) ? valOrUpdater(oldValue) : valOrUpdater;
        return fnEquals(oldValue, value) ? oldValue : value;
      });
    },
    [fnEquals, setNewValue]
  );
  // Returns either previous value or current value based on equality comparison, along with setter
  if (prevValueRef.current === undefined || !fnEquals(prevValueRef.current, newValue)) {
    prevValueRef.current = newValue;
    return [newValue, setNewValueWrapped];
  } else {
    return [prevValueRef.current, setNewValueWrapped];
  }
}

/*
Equal Recoil End
*/

/**
 * Makes readonly EqualSelectors for any property in a given object
 */
export function makePropertyStates<T, K extends keyof T>(name: string, prop: K, recoilValue: RecoilValueReadOnly<T>, fnEquals: FnEquals<T[K]> = FN_EQUALS_STRICT) {
  return equalSelectorReadonly<T[K]>({
    key: `${name}_${prop.toString()}`,
    fnEquals,
    get: ({ get }) => get(recoilValue)[prop]
  });
}
